import React from 'react';
import { FROM, SQL_FROM, CSV_FROM, EXPAND } from '@/util/enums/EnumLayers';
import OnboardingButton from './OnboardingButton';
import TooltipWrapper from '../TooltipWrapper';

let idCounter = 1; // used to give each layer a unique id

abstract class Layer {
  _id: number;

  _enabled: boolean;

  _active: boolean;

  suggestion: boolean;

  constructor(readonly _type: string) {
    this._id = idCounter++;
    this._enabled = true;
    this._active = true;
    this.suggestion = false;
  }

  abstract buildContent(editLayerClicked: F0, aliases?: any): JSX.Element;

  static markIdAsInUse(id: number) {
    if (id >= idCounter) {
      idCounter = id + 1;
    }
  }

  activate() {
    this._active = true;
  }

  deactivate() {
    this._active = false;
  }

  id() {
    return this._id;
  }

  enabled() {
    return this._enabled;
  }

  /*
   * return true iff this step is currently just for show (i.e. it's a template that user still needs to fill in),
   *   and as such, server should effectively ignore it for building SQL
   */
  isTemplate() {
    return false;
  }

  /*
   * the layer type for the template
   */
  layerType() {
    // this is needed if isTemplate() returns true
    return this.type();
  }

  cloneAndupdateEnabled(enabled: boolean) {
    const copy = Object.assign(Object.create(this.constructor.prototype), this);
    copy._enabled = enabled;
    return copy;
  }

  cloneAndupdateActive(active: boolean) {
    const copy = Object.assign(Object.create(this.constructor.prototype), this);
    copy._active = active;
    return copy;
  }

  cloneAndUpdateSuggestion(isSuggestion: boolean) {
    const copy = Object.assign(Object.create(this.constructor.prototype), this);
    copy.suggestion = isSuggestion;
    return copy;
  }

  cloneAndUpdateId(id: number) {
    const copy = Object.assign(Object.create(this.constructor.prototype), this);
    copy._id = id;
    return copy;
  }

  type() {
    return this._type;
  }

  html(
    layerInteraction: any,
    editLayerClicked: any,
    isActiveLayer: any,
    layerNo: any,
    isLastLayer: any,
    disableSqlEdit: any,
    aliases: any,
    stepCount: any,
  ) {
    let layerClass = 'layer';
    let turnOnClass =
      'glyphicon glyphicon-unchecked layer_turn-on padding-left-10';
    let turnOffClass =
      'glyphicon glyphicon-check layer_turn-off padding-left-10';
    let removeClass = 'glyphicon glyphicon-remove layer_remove padding-left-10';
    let editClass = 'glyphicon glyphicon-pencil layer_edit padding-right-10';
    let collapseClass = 'glyphicon glyphicon-triangle-top padding-left-10';

    if (this.isTemplate()) {
      // template layers aren't disable-able, they're just deletable
      turnOnClass = 'hide';
      turnOffClass = 'hide';
      editClass = 'hide';
    } else if (this.enabled()) {
      turnOnClass = 'hide';
      removeClass = 'hide';
    } else {
      turnOffClass = 'hide';
      layerClass += ' toggled-off';
    }
    if (!isActiveLayer) {
      turnOnClass = 'hide';
      turnOffClass = 'hide';
      removeClass = 'hide';
      editClass = 'hide';
      collapseClass = 'hide';
    }
    switch (this.type()) {
      case FROM:
      case CSV_FROM:
      case SQL_FROM:
        turnOffClass = 'hide';
        collapseClass = 'hide';
        if (disableSqlEdit && this.type() === SQL_FROM) {
          editClass = 'hide';
          editLayerClicked = () => {}; // disable edit
        }
    }
    const content = this.buildContent(editLayerClicked, aliases); // call subclass

    let editButton: Nullable<JSX.Element> = (
      <TooltipWrapper tooltip="edit step">
        <OnboardingButton
          onboarding={layerNo === 1 ? 'edit' : null}
          className={editClass}
          onClick={editLayerClicked}
        />
      </TooltipWrapper>
    );
    if (this.type() === EXPAND) {
      editButton = null; // can't edit this layer
    }
    const collapseLayers = (e: React.MouseEvent) => {
      layerInteraction('collapse', { layerId: this.id() })(e);
      window.scrollTo(0, 0);
    };

    const stepNo = layerNo + stepCount + 1;

    let stepClass = 'step-connector';
    if (stepNo === 1) {
      stepClass = 'first-step-connector';
    }

    return (
      <div
        key={this.id()}
        className={layerClass}
        onClick={layerInteraction('selected', { layerId: this.id() })}
      >
        <div className="layer-container">
          {!this.enabled() ? null : isLastLayer ? null : (
            <span className="layer-hover-text btn btn-outline-dark btn-xs">
              jump to step
            </span>
          )}
          <div className={stepClass} />
          <div className="step-Number">{stepNo}</div>
          {editButton}
          <span className="layer_text">{content}</span>
          <div className="layer-control">
            <TooltipWrapper tooltip="delete this step">
              <span
                className={removeClass}
                onClick={layerInteraction('deleted', { layerId: this.id() })}
              />
            </TooltipWrapper>
            <TooltipWrapper tooltip="disable this step">
              <OnboardingButton
                onboarding={isLastLayer ? 'disable' : null}
                className={turnOffClass}
                onClick={layerInteraction('toggled', { layerId: this.id() })}
              />
            </TooltipWrapper>
            <TooltipWrapper tooltip="enable this step">
              <span
                className={turnOnClass}
                onClick={layerInteraction('toggled', { layerId: this.id() })}
              />
            </TooltipWrapper>
            <TooltipWrapper tooltip="collapse steps above">
              <span className={collapseClass} onClick={collapseLayers} />
            </TooltipWrapper>
          </div>
        </div>
      </div>
    );
  }
}

export default Layer;
